import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CheckCircle, ExclamationTriangle, InfoCircle, LightningCharge, PlusCircle, Trash } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import { fetchJsonOrThrow } from '../utils/fetchJson';
import {
  formatIngredientLabel,
  formatTimeRange,
  getRecipeImageCandidates,
  normalize,
} from '../utils/recipes';
import {
  createPantryEntry,
  formatPantryUpdatedAt,
  getPantryKey,
  getPantryFreshnessStatus,
  loadPantryState,
  pantryStateToItems,
  savePantryState,
} from '../utils/pantryStorage';
import { buildRecipePriceComparison } from '../utils/recipePricing';
import { loadProfile, saveProfile } from '../utils/profileStorage';
import api_url from '../config/api_url';

const PANTRY_UNITS = ['g', 'stk', 'ml', 'fed', 'spsk', 'tsk'];
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 420'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='0' y2='1'%3E%3Cstop offset='0%25' stop-color='%23f7ecdf'/%3E%3Cstop offset='100%25' stop-color='%23e6d7c4'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='640' height='420' fill='url(%23g)'/%3E%3C/svg%3E";

function buildAmountLabel(amount, unit) {
    const quantity = [normalize(amount), normalize(unit)].filter(Boolean).join(' ').trim();
    return quantity ? `${quantity} tilbage` : 'Rest tilbage';
}

function getPantrySourceLabel(source, trackingMode) {
  if (source === 'leftover') return trackingMode === 'smart' ? 'Smart rest' : 'Rest';
  if (source === 'shopping') return trackingMode === 'smart' ? 'Smart køb' : 'Købt';
  return 'Tilføjet';
}

function buildRecipeSuggestions(recipes, pantryItems) {
  if (!Array.isArray(recipes) || pantryItems.length === 0) {
    return [];
  }

  return recipes
    .map((recipe) => {
      const priceComparison = buildRecipePriceComparison(recipe, { pantryItems });

      return {
        recipe,
        matchedItems: priceComparison.matchedItems,
        priceComparison,
      };
    })
    .filter((suggestion) => suggestion.matchedItems.length > 0)
    .sort((left, right) => {
      if (right.matchedItems.length !== left.matchedItems.length) {
        return right.matchedItems.length - left.matchedItems.length;
      }
      return normalize(left.recipe.name).localeCompare(normalize(right.recipe.name), 'da-DK');
    })
    .slice(0, 6);
}

function Pantry() {
  const [pantryState, setPantryState] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [productName, setProductName] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('g');
  const [note, setNote] = useState('');
  const [isClearConfirmationVisible, setIsClearConfirmationVisible] = useState(false);
  const [inventoryMode, setInventoryMode] = useState(() => loadProfile().inventoryMode);
  const [isModeDetailsOpen, setIsModeDetailsOpen] = useState(false);

  useEffect(() => {
    setPantryState(loadPantryState());

    fetchJsonOrThrow(api_url + 'api/recipes')
      .then((data) => setRecipes(Array.isArray(data) ? data : []))
      .catch(() => setRecipes([]));
  }, []);

  const pantryItems = useMemo(() => pantryStateToItems(pantryState), [pantryState]);
  const recipeSuggestions = useMemo(
    () => buildRecipeSuggestions(recipes, pantryItems),
    [recipes, pantryItems]
  );
  const freshnessByKey = useMemo(
    () => new Map(pantryItems.map((item) => [item.key, getPantryFreshnessStatus(item)])),
    [pantryItems]
  );
  const freshnessReviewCount = pantryItems.filter(
    (item) => freshnessByKey.get(item.key)?.requiresConfirmation
  ).length;

  const updatePantry = (nextState) => {
    setPantryState(nextState);
    savePantryState(nextState);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const key = getPantryKey(productName);
    if (!key) {
      return;
    }

    updatePantry({
      ...pantryState,
      [key]: createPantryEntry({
        productName,
        amountLabel: buildAmountLabel(amount, unit),
        source: 'manual',
        note,
        quantityAmount: amount,
        quantityUnit: unit,
      }),
    });
    setProductName('');
    setAmount('');
    setUnit('g');
    setNote('');
  };

  const removeItem = (key) => {
    const nextState = { ...pantryState };
    delete nextState[key];
    updatePantry(nextState);
  };

  const confirmItem = (key) => {
    const currentItem = pantryState[key];
    if (!currentItem) {
      return;
    }

    const timestamp = new Date().toISOString();
    updatePantry({
      ...pantryState,
      [key]: {
        ...currentItem,
        confirmedAt: timestamp,
      },
    });
  };

  const clearPantry = () => {
    updatePantry({});
    setIsClearConfirmationVisible(false);
  };

  const handleInventoryModeChange = (nextMode) => {
    setInventoryMode(nextMode);
    setIsModeDetailsOpen(false);
    saveProfile({
      ...loadProfile(),
      inventoryMode: nextMode,
    });
  };

  const confirmAllItems = () => {
    const timestamp = new Date().toISOString();
    const nextState = Object.fromEntries(Object.entries(pantryState).map(([key, item]) => [
      key,
      freshnessByKey.get(key)?.requiresConfirmation
        ? { ...item, confirmedAt: timestamp }
        : item,
    ]));
    updatePantry(nextState);
  };

  return (
    <div className="pantry-page">
      <header className="pantry-page-header">
        <div>
          <p className="pantry-kicker">Dit spisekammer</p>
          <h1>Rester og hjemmevarer</h1>
          <p>Gemte varer fra opskrifter og manuelle tilføjelser samles her.</p>
        </div>
        <div className="pantry-status-strip" aria-label="Spisekammer status">
          <span>
            <strong>{pantryItems.length}</strong>
            varer
          </span>
          <span className={freshnessReviewCount > 0 ? 'needs-review' : ''}>
            <strong>{freshnessReviewCount}</strong>
            skal tjekkes
          </span>
        </div>
      </header>

      <details className="pantry-local-storage-notice">
        <summary>
          <InfoCircle aria-hidden="true" />
          Hvor gemmes mit spisekammer?
        </summary>
        <p>Dit spisekammer gemmes på denne enhed og synkroniseres endnu ikke mellem enheder.</p>
      </details>

      <section className={`pantry-mode-card${inventoryMode === 'smart' ? ' is-smart' : ''}${isModeDetailsOpen ? ' is-open' : ''}`} aria-label="Styring af spisekammer">
        <span className="pantry-mode-icon" aria-hidden="true"><LightningCharge /></span>
        <span className="pantry-mode-copy">
          <strong>{inventoryMode === 'smart' ? 'Smart spisekammer er slået til' : 'Præcis registrering er slået til'}</strong>
          <small>
            {inventoryMode === 'smart'
              ? 'Køb og rester kan registreres samlet. Automatiske mængder vises tydeligt som estimater.'
              : 'Du bekræfter fortsat køb, forbrug og rester enkeltvis.'}
          </small>
        </span>
        <button
          type="button"
          className="pantry-mode-change"
          onClick={() => setIsModeDetailsOpen((isOpen) => !isOpen)}
          aria-expanded={isModeDetailsOpen}
          aria-controls="pantry-mode-options"
        >
          {isModeDetailsOpen ? 'Luk' : 'Skift'}
        </button>
        {isModeDetailsOpen && (
          <div id="pantry-mode-options" className="pantry-mode-options">
            <span className="pantry-mode-comparison">
              <span><b>Smart:</b> Bekræft hele købet og den færdige ret. Vi beregner resten.</span>
              <span><b>Præcis:</b> Vælg købte, brugte og gemte varer enkeltvis.</span>
            </span>
            <span className="pantry-mode-toggle" role="group" aria-label="Vælg styring af spisekammer">
              <button type="button" className={inventoryMode === 'smart' ? 'is-active' : ''} aria-pressed={inventoryMode === 'smart'} onClick={() => handleInventoryModeChange('smart')}>Smart</button>
              <button type="button" className={inventoryMode === 'precise' ? 'is-active' : ''} aria-pressed={inventoryMode === 'precise'} onClick={() => handleInventoryModeChange('precise')}>Præcis</button>
            </span>
          </div>
        )}
      </section>

      <div className="pantry-layout">
        <section className="pantry-panel pantry-panel-main">
          <div className="pantry-panel-header">
            <div>
              <h2>Gemte varer</h2>
              {!pantryItems.length && <p>Ingen varer gemt endnu.</p>}
            </div>
          </div>

          {freshnessReviewCount > 0 && (
            <div className="pantry-freshness-explanation">
              <span>{`${freshnessReviewCount} ${freshnessReviewCount === 1 ? 'vare skal' : 'varer skal'} bekræftes, så de ikke påvirker næste indkøb ved en fejl.`}</span>
              {inventoryMode === 'smart' && (
                <button type="button" onClick={confirmAllItems}>
                  <CheckCircle size={14} aria-hidden="true" />
                  Ja, behold alle
                </button>
              )}
            </div>
          )}

          {pantryItems.length > 0 ? (
            <ul className="pantry-items-list">
              {pantryItems.map((item) => {
                const freshnessStatus = freshnessByKey.get(item.key);
                const needsConfirmation = Boolean(freshnessStatus?.requiresConfirmation);

                return (
                <li key={item.key} className={`pantry-item-card${needsConfirmation ? ' needs-confirmation' : ''}`}>
                  <div className="pantry-item-main">
                    <span className="pantry-item-source">
                      {`${getPantrySourceLabel(item.source, item.trackingMode)}${item.confidence === 'estimated' ? ' · estimat' : ''}`}
                    </span>
                    <strong>{formatIngredientLabel(item.productName)}</strong>
                    <span>{item.amountLabel || 'Rest tilbage'}</span>
                    {(item.note || item.updatedAt) && (
                      <details className="pantry-item-details">
                        <summary>Detaljer</summary>
                        {item.note ? <em>{item.note}</em> : null}
                        {item.updatedAt ? <span>{formatPantryUpdatedAt(item.updatedAt)}</span> : null}
                      </details>
                    )}
                    {needsConfirmation && (
                      <div className="pantry-freshness-prompt">
                        <span className="pantry-freshness-icon" aria-hidden="true">
                          <ExclamationTriangle size={15} />
                        </span>
                        <span>
                          <strong>Har du stadig denne vare?</strong>
                          <small>{`Sidst bekræftet for ${freshnessStatus.ageDays} dage siden.`}</small>
                        </span>
                        <span className="pantry-freshness-actions">
                          <button type="button" onClick={() => confirmItem(item.key)}>
                            <CheckCircle size={14} aria-hidden="true" />
                            Ja, jeg har den
                          </button>
                          <button type="button" onClick={() => removeItem(item.key)}>
                            Nej, fjern
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="pantry-item-meta">
                    <button
                      type="button"
                      className="pantry-icon-button"
                      onClick={() => removeItem(item.key)}
                      aria-label={`Fjern ${formatIngredientLabel(item.productName)} fra spisekammeret`}
                    >
                      <Trash size={16} aria-hidden="true" />
                    </button>
                  </div>
                </li>
                );
              })}
            </ul>
          ) : (
            <div className="pantry-empty-state">
              <p>Gem rester fra en opskrift, eller tilføj en vare manuelt.</p>
              <Link to="/recipes">Find en opskrift</Link>
            </div>
          )}
        </section>

        <section className="pantry-panel pantry-panel-tool">
          <div className="pantry-panel-header">
            <div>
              <h2>Tilføj vare</h2>
            </div>
          </div>
          <form className="pantry-add-form" onSubmit={handleSubmit}>
            <label>
              <span>Vare</span>
              <input
                type="text"
                value={productName}
                onChange={(event) => setProductName(event.target.value)}
                placeholder="fx løg"
              />
            </label>
            <div className="pantry-form-row">
              <label>
                <span>Mængde</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="300"
                />
              </label>
              <label>
                <span>Enhed</span>
                <select value={unit} onChange={(event) => setUnit(event.target.value)}>
                  {PANTRY_UNITS.map((unitOption) => (
                    <option key={unitOption} value={unitOption}>
                      {unitOption}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <details className="pantry-add-note">
              <summary>Tilføj note (valgfrit)</summary>
              <label>
                <span>Note</span>
                <input
                  type="text"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="fx åbnet i dag"
                />
              </label>
            </details>
            <button type="submit" className="pantry-add-button">
              <PlusCircle size={17} aria-hidden="true" />
              <span>Gem i spisekammer</span>
            </button>
          </form>
        </section>
      </div>

      <section className="pantry-panel pantry-suggestions-panel">
        <div className="pantry-panel-header">
          <div>
            <h2>Opskrifter der passer til dine rester</h2>
            {!recipeSuggestions.length && <p>Gem en vare for at se forslag.</p>}
          </div>
          <Link to="/recipes" className="pantry-panel-link">
            Alle opskrifter
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>

        {recipeSuggestions.length > 0 ? (
          <div className="pantry-suggestions-grid">
            {recipeSuggestions.map(({ recipe, matchedItems, priceComparison }) => {
              const imageCandidates = getRecipeImageCandidates(recipe);
              const imageSrc = imageCandidates[0] || PLACEHOLDER_IMAGE;
              const matchedNames = matchedItems
                .map((item) => formatIngredientLabel(item.productName))
                .slice(0, 3)
                .join(', ');
              const secondaryMeta = priceComparison.fullWeeklyLabel
                ? `Uden spisekammer ca. ${priceComparison.fullWeeklyLabel}`
                : formatTimeRange(recipe.tid_min, recipe.tid_max);

              return (
                <Link
                  key={recipe.recipe_id}
                  to={`/recipes/${recipe.recipe_id}`}
                  className="pantry-recipe-card"
                >
                  <img src={imageSrc} alt="" loading="lazy" />
                  <div className="pantry-recipe-card-body">
                    <span>{matchedItems.length} match</span>
                    <strong>{recipe.name}</strong>
                    <em>{matchedNames}</em>
                    {(priceComparison.hasPantrySavings || priceComparison.hasCompletePantryCoverage) ? (
                      <div className="pantry-recipe-price-compare">
                        <small>
                          <span>Uden spisekammer</span>
                          <strong>{priceComparison.fullWeeklyLabel}</strong>
                        </small>
                        <small className="pantry-recipe-price">
                          <span>Med dit spisekammer</span>
                          <strong>{priceComparison.pantryWeeklyLabel}</strong>
                        </small>
                      </div>
                    ) : secondaryMeta ? (
                      <small>{secondaryMeta}</small>
                    ) : null}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="pantry-empty-state pantry-empty-state-inline">
            <p>Gemte rester bliver vist her som opskriftsforslag.</p>
          </div>
        )}
      </section>

      {pantryItems.length > 0 && (
        <section className="pantry-clear-section" aria-labelledby="pantry-clear-heading">
          <div>
            <h2 id="pantry-clear-heading">Ryd spisekammeret</h2>
            <p>Fjern alle {pantryItems.length} gemte varer på én gang.</p>
          </div>
          {!isClearConfirmationVisible ? (
            <button
              type="button"
              className="pantry-clear-trigger"
              onClick={() => setIsClearConfirmationVisible(true)}
            >
              <Trash size={16} aria-hidden="true" />
              Slet alle varer
            </button>
          ) : (
            <div className="pantry-clear-confirmation" role="alert">
              <strong>Er du sikker?</strong>
              <span>Alle gemte varer og mængder slettes. Handlingen kan ikke fortrydes.</span>
              <div>
                <button type="button" onClick={() => setIsClearConfirmationVisible(false)}>
                  Annuller
                </button>
                <button type="button" className="is-destructive" onClick={clearPantry}>
                  Ja, slet alle
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default Pantry;
